import React, { useRef, useEffect, useState } from 'react';
import { Stage, Layer, Rect, Line } from 'react-konva';
import { Box, Paper } from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { isGroupElement, LabelElement } from '../../types';
import ElementRenderer from './ElementRenderer';
import GroupRenderer from './GroupRenderer';
import { SalesOrder } from 'services/salesOrders';

// Grid settings
const GRID_SIZE = 12; // Size of grid cells in pixels
const SNAP_THRESHOLD = 6; // Distance in pixels to trigger snapping
const PRIMARY_GRID_SIZE = GRID_SIZE; // Size of primary grid (20px)

const EDGE_OFFSET_BOTTOM = 1.5;
const EDGE_OFFSET_RIGHT = 2.5;

interface DesignerCanvasProps {
  stageRef: React.RefObject<any>;
  elements: LabelElement[];
  selectedElement: LabelElement | null;
  setSelectedElement: (element: LabelElement | null) => void;
  selectedElements: LabelElement[];
  setSelectedElements: (elements: LabelElement[]) => void;
  onUpdateElement: (id: string, updates: Partial<LabelElement>) => void;
  isDragging: boolean;
  previewMode?: boolean;
  previewData: SalesOrder | null;
  showGrid?: boolean;
  canvasWidth: number;
  canvasHeight: number;
  currentPage: number;
}

const DesignerCanvas: React.FC<DesignerCanvasProps> = ({
  stageRef,
  elements,
  selectedElement,
  setSelectedElement,
  selectedElements,
  setSelectedElements,
  onUpdateElement,
  isDragging,
  previewMode = false,
  previewData,
  showGrid = true,
  canvasWidth,
  canvasHeight,
  currentPage,
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectionBox, setSelectionBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const layerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { setNodeRef } = useDroppable({
    id: 'canvas',
  });

  // Function to snap a value to the grid and respect canvas boundaries
  const snapToGrid = (value: number, max: number): number => {
    // First snap to the smaller grid
    const snappedToSmallGrid = Math.round(value / GRID_SIZE) * GRID_SIZE;

    // Check if we're close to a primary grid line
    const distanceToPrimaryGrid = snappedToSmallGrid % PRIMARY_GRID_SIZE;
    const snappedValue =
      distanceToPrimaryGrid <= SNAP_THRESHOLD
        ? Math.round(snappedToSmallGrid / PRIMARY_GRID_SIZE) * PRIMARY_GRID_SIZE
        : snappedToSmallGrid;

    // Ensure the value stays within canvas boundaries
    return Math.max(0, Math.min(snappedValue, max));
  };

  // Function to handle element snapping with boundaries
  const handleSnapping = (
    element: LabelElement,
    updates: Partial<LabelElement>
  ) => {
    const snappedUpdates = { ...updates };

    if ('x' in updates) {
      const newX = updates.x ?? element.x; // fall back to current value
      // Ensure x position respects canvas boundaries
      snappedUpdates.x = snapToGrid(
        newX,
        canvasWidth - (element.width ?? 0) - EDGE_OFFSET_RIGHT
      );
    }
    if ('y' in updates) {
      snappedUpdates.y = snapToGrid(
        updates.y!,
        canvasHeight - (element.height || 0) - EDGE_OFFSET_BOTTOM
      );
    }
    if ('width' in updates) {
      const maxWidth = canvasWidth - (element.x || 0) - EDGE_OFFSET_RIGHT;
      snappedUpdates.width = snapToGrid(updates.width!, maxWidth);
    }
    if ('height' in updates) {
      const maxHeight = canvasHeight - (element.y || 0) - EDGE_OFFSET_BOTTOM;
      snappedUpdates.height = snapToGrid(updates.height!, maxHeight);
    }

    return snappedUpdates;
  };

  const getRelativePointerPosition = (stage: any) => {
    const transform = stage.getAbsoluteTransform().copy();
    transform.invert();
    const pos = stage.getPointerPosition();
    if (!pos) {
      return { x: 0, y: 0 }; // or abort the operation
    }
    return transform.point(pos);
  };

  const handleStageMouseDown = (e: any) => {
    if (previewMode) return;

    const clickedOnEmpty =
      e.target === e.target.getStage() ||
      e.target.attrs.id === 'background-rect';

    if (clickedOnEmpty) {
      setSelectedElement(null);
      const stage = stageRef.current;
      const pos = getRelativePointerPosition(stage);

      setStartPoint(pos);
      setIsSelecting(true);
      setSelectionBox({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
      });
      setSelectedElements([]);
    }
  };

  const handleStageMouseMove = () => {
    if (!isSelecting || !startPoint) return;

    const stage = stageRef.current;
    const pos = getRelativePointerPosition(stage);

    setSelectionBox({
      x: Math.min(startPoint.x, pos.x),
      y: Math.min(startPoint.y, pos.y),
      width: Math.abs(pos.x - startPoint.x),
      height: Math.abs(pos.y - startPoint.y),
    });
  };

  const handleStageMouseUp = () => {
    if (!selectionBox || !isSelecting) return;

    const selected = elements.filter((element) => {
      const elementRight = element.x + element.width;
      const elementBottom = element.y + element.height;
      const boxRight = selectionBox.x + selectionBox.width;
      const boxBottom = selectionBox.y + selectionBox.height;

      return (
        element.x < boxRight &&
        elementRight > selectionBox.x &&
        element.y < boxBottom &&
        elementBottom > selectionBox.y
      );
    });

    setSelectedElements(selected.map((el) => el));
    if (selected.length === 1) {
      setSelectedElement(selected[0]);
    }

    setIsSelecting(false);
    setSelectionBox(null);
    setStartPoint(null);
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && stageRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;

        const scaleX = containerWidth / canvasWidth;
        const scaleY = containerHeight / canvasHeight;
        const newScale = Math.min(scaleX, scaleY) * 0.9;

        setScale(newScale);

        const newX = (containerWidth - canvasWidth * newScale) / 2;
        const newY = (containerHeight - canvasHeight * newScale) / 2;

        setPosition({ x: newX, y: newY });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [stageRef, canvasWidth, canvasHeight]);

  // Generate grid lines
  const gridLines = [];
  if (showGrid && !previewMode) {
    // Vertical lines
    for (let x = 0; x <= canvasWidth; x += GRID_SIZE) {
      const isPrimary = x % PRIMARY_GRID_SIZE === 0;
      gridLines.push(
        <Line
          key={`v${x}`}
          points={[x, 0, x, canvasHeight]}
          stroke="#ddd"
          strokeWidth={isPrimary ? 0.5 : 0.25}
          opacity={isPrimary ? 0.5 : 0.25}
          name="grid-line"
        />
      );
    }
    // Horizontal lines
    for (let y = 0; y <= canvasHeight; y += GRID_SIZE) {
      const isPrimary = y % PRIMARY_GRID_SIZE === 0;
      gridLines.push(
        <Line
          key={`h${y}`}
          points={[0, y, canvasWidth, y]}
          stroke="#ddd"
          strokeWidth={isPrimary ? 0.5 : 0.25}
          opacity={isPrimary ? 0.5 : 0.25}
          name="grid-line"
        />
      );
    }
  }

  // Add page break lines
  const pageBreakLines = [];
  for (
    let i = 1;
    i < Math.ceil(canvasHeight / (canvasHeight / currentPage));
    i++
  ) {
    pageBreakLines.push(
      <Line
        key={`page-break-${i}`}
        points={[
          0,
          i * (canvasHeight / currentPage),
          canvasWidth,
          i * (canvasHeight / currentPage),
        ]}
        stroke="#2196f3"
        strokeWidth={2}
        dash={[10, 5]}
      />
    );
  }

  return (
    <Box
      ref={(node) => {
        setNodeRef(node as HTMLDivElement);
        containerRef.current = node as HTMLDivElement | null;
      }}
      id="label-canvas"
      sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f5f5f5',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: canvasWidth * scale,
          height: canvasHeight * scale,
          overflow: 'hidden',
          borderRadius: 1,
          transition: 'box-shadow 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <Stage
          ref={stageRef}
          width={canvasWidth * scale}
          height={canvasHeight * scale}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          onTouchStart={handleStageMouseDown}
          onTouchMove={handleStageMouseMove}
          onTouchEnd={handleStageMouseUp}
          scaleX={scale}
          scaleY={scale}
        >
          <Layer ref={layerRef}>
            <Rect
              id="background-rect"
              x={0}
              y={0}
              width={canvasWidth}
              height={canvasHeight}
              fill="#ffffff"
            />

            {gridLines}
            {pageBreakLines}

            {elements.map((element) =>
              isGroupElement(element) ? (
                <GroupRenderer
                  key={element.id}
                  group={element}
                  isSelected={
                    selectedElement?.id === element.id ||
                    selectedElements.includes(element)
                  }
                  onSelect={() => !previewMode && setSelectedElement(element)}
                  onChange={(newAttrs) => {
                    const snappedAttrs = handleSnapping(element, newAttrs);
                    onUpdateElement(element.id, snappedAttrs);
                  }}
                  previewMode={previewMode}
                  previewData={previewData}
                  gridSize={GRID_SIZE}
                />
              ) : (
                <ElementRenderer
                  key={element.id}
                  element={element}
                  isSelected={
                    selectedElement?.id === element.id ||
                    selectedElements.includes(element)
                  }
                  onSelect={() => {
                    if (previewMode) return;
                    setSelectedElement(element);
                    setSelectedElements([element]);
                  }}
                  onChange={(newAttrs) => {
                    const snappedAttrs = handleSnapping(element, newAttrs);
                    onUpdateElement(element.id, snappedAttrs);
                  }}
                  previewMode={previewMode}
                  previewData={previewData}
                  gridSize={GRID_SIZE}
                />
              )
            )}

            {selectionBox && (
              <Rect
                x={selectionBox.x}
                y={selectionBox.y}
                width={selectionBox.width}
                height={selectionBox.height}
                fill="rgba(33, 150, 243, 0.1)"
                stroke="#2196f3"
                strokeWidth={1}
              />
            )}
          </Layer>
        </Stage>
      </Paper>

      {isDragging && !previewMode && (
        <Box
          sx={{
            position: 'absolute',
            top: position.y,
            left: position.x,
            width: canvasWidth * scale,
            height: canvasHeight * scale,
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            zIndex: 10,
            pointerEvents: 'none',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 1,
          }}
        />
      )}
    </Box>
  );
};

export default DesignerCanvas;
